import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { StorageMap } from '@ngx-pwa/local-storage';
import { TranslateService } from '@ngx-translate/core';
import { locale as moment_locale } from 'moment';
import { HeaderService, ITenant } from 'onego-header';
import { Observable, combineLatest, firstValueFrom, from, interval, lastValueFrom, of } from 'rxjs';
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    mergeMap,
    shareReplay,
    switchMap,
    take,
    tap,
} from 'rxjs/operators';
import {
    IAcceptInvitationsByEmailResponse,
    IActor,
    IIdentityClaims,
    IRole,
    IUser,
    IUserTenantInvitation,
    StateValue,
    UserApiService,
} from 'shared';

import { UserFilter } from '../../models/user-filter.model';
import { allowAll } from '../../utils/allow-all.filter';
import { Feature } from '../models';
import { ISocketStatus } from './socket.service';
import { AppStateService } from './state';
import { TenantService } from './tenant.service';

type TAsyncUser = Observable<IUser> | StateValue<IUser>;

@Injectable({ providedIn: 'root' })
export class UserService {
    private socketStatus: ISocketStatus['status'];

    private _hasPermission = {
        get: (user: IUser, permission: string): boolean =>
            user ? this.containsNode(permission, user.permissions ?? []) : false,
        stream: (user: TAsyncUser, permission: string): Observable<boolean> =>
            this.observable(user).pipe(map((u) => this._hasPermission.get(u, permission))),
    };

    private _hasPermissions = {
        get: (user: IUser, permissions: Array<string>, all = true): boolean => {
            const _permissions = permissions.map((p) => this._hasPermission.get(user, p));
            return all ? _permissions.every(Boolean) : _permissions.some(Boolean);
        },
        stream: (user: TAsyncUser, permissions: Array<string>, all = true): Observable<boolean> =>
            combineLatest(permissions.map((p) => this._hasPermission.stream(user, p))).pipe(
                map((_permissions) => (all ? _permissions.every(Boolean) : _permissions.some(Boolean)))
            ),
    };

    private _hasFeature = {
        get: (user: IUser, feature: Feature): boolean => (user ? this.containsNode(feature, user.features) : false),
        stream: (user: TAsyncUser, feature: Feature): Observable<boolean> =>
            this.observable(user).pipe(map((u) => this._hasFeature.get(u, feature))),
    };

    private _hasFeatures = {
        get: (user: IUser, features: Array<Feature>, all = true): boolean => {
            const _features = features.map((p) => this._hasFeature.get(user, p));
            return all ? _features.every(Boolean) : _features.some(Boolean);
        },
        stream: (user: TAsyncUser, features: Array<Feature>, all = true): Observable<boolean> =>
            combineLatest(features.map((p) => this._hasFeature.stream(user, p))).pipe(
                map((_features) => (all ? _features.every(Boolean) : _features.some(Boolean)))
            ),
    };

    private acceptInvitationsByEmail: Observable<IAcceptInvitationsByEmailResponse>;

    private readonly headerService = inject(HeaderService);
    private readonly userApi = inject(UserApiService);
    private readonly translate = inject(TranslateService);
    private readonly storage = inject(StorageMap);
    private readonly tenantService = inject(TenantService);
    private readonly appState = inject(AppStateService);

    public constructor() {
        this.headerService.onInitialized
            .pipe(
                filter(Boolean),
                take(1),
                tap((val) =>
                    console.debug('UserService listening to OIDC Auth Service initialization.\nInitialized:', val)
                ),
                tap(() => this.initialize())
            )
            .subscribe();

        this.acceptInvitationsByEmail = this.userApi.acceptInvitationsByEmail().pipe(
            debounceTime(100),
            shareReplay(1),
            switchMap((response) =>
                // If a new user was created, refresh the token to get the MtpUserId claim
                response.newUserCreated && !(this.headerService.getClaims() as IIdentityClaims).MtpUserId
                    ? from(this.headerService.refreshToken()).pipe(map(() => response))
                    : of(response)
            )
        );
    }

    public hasPermissionSync = (permission: string): boolean =>
        this._hasPermission.get(this.appState.user.value, permission);

    public hasPermission = (permission: string): Observable<boolean> =>
        this._hasPermission.stream(this.appState.user, permission);

    public hasPermissionsSync = (permissions: Array<string>, all = true): boolean =>
        this._hasPermissions.get(this.appState.user.value, permissions, all);

    public hasPermissions = (permissions: Array<string>, all = true): Observable<boolean> =>
        this._hasPermissions.stream(this.appState.user, permissions, all);

    public hasOnlyTeamPermission = (...permissions: Array<string>): Observable<boolean> =>
        combineLatest(
            permissions.map((permission) =>
                combineLatest([
                    this.hasPermission(`${permission}.Teams`),
                    this.hasPermission(`${permission}.All`),
                ]).pipe(map(([ownTeams, all]) => ownTeams && !all))
            )
        ).pipe(map((teamScoped) => teamScoped.some(Boolean)));

    public hasFeature = (feature: Feature): Observable<boolean> => this._hasFeature.stream(this.appState.user, feature);

    public hasFeatures = (features: Array<Feature>, all = true): Observable<boolean> =>
        this._hasFeatures.stream(this.appState.user, features, all);

    public useLanguage(language: string): Observable<unknown> {
        // Change locale in moment library
        moment_locale(language);
        // Set translation locale
        return this.translate.use(language);
    }

    public saveLanguage(language: string, saveToProfile = true): void {
        language ??= 'en';
        this.storage.set('USER_LANGUAGE', language).pipe(take(1)).subscribe();
        this.useLanguage(language).pipe(take(1)).subscribe();
        if (!saveToProfile) return;
        // Save preference to profile
        const user = this.appState.user.value;
        if (!user) return;
        this.userApi.putProfile(user.id, { language }).subscribe();
    }

    public getLanguage(): Observable<string> {
        return this.storage.get<string>('USER_LANGUAGE', { type: 'string' });
    }

    public checkPermission(permission: string): Promise<boolean> {
        return firstValueFrom(this._hasPermission.stream(this.appState.user, permission));
    }

    public checkPermissions(permissions: Array<string>, all = true): Promise<boolean> {
        return firstValueFrom(this._hasPermissions.stream(this.appState.user, permissions, all));
    }

    public filterUsers(filter: UserFilter): Observable<Array<IUser>> {
        return this.appState.users.stream.pipe(
            switchMap((users) =>
                combineLatest(
                    users.map((user) => (filter || allowAll)(user).pipe(map((enabled) => ({ user, enabled }))))
                )
            ),
            map((userResults) =>
                userResults.filter((userResult) => userResult.enabled).map((userResult) => userResult.user)
            )
        );
    }

    public getFullName(noActor: string, userId?: string): Observable<string> {
        return this.getUserFullName(userId ? { type: 'PlatformUser', userId } : null, 'misc.unknownUser', noActor);
    }

    public getUserFullName(
        actor?: IActor,
        defaultNameUnknownActor?: string,
        defaultNameNoActor?: string
    ): Observable<string> {
        if (!actor && defaultNameNoActor) return this.translate.stream(defaultNameNoActor);
        return this.getActorName(actor, this.translate.instant(defaultNameUnknownActor));
    }

    public getUserName(id: string, fallback: string = null): Observable<string> {
        return this.getActorName({ type: 'PlatformUser', userId: id }, fallback);
    }

    public getActorName(actor: IActor, fallback: string = null): Observable<string | null> {
        fallback ??= this.translate.instant('mapView.detail.comments.users.unknown');

        const name$ =
            actor?.type === 'PlatformUser' || actor?.type === 'TokenUser'
                ? this.getUser(actor.userId).pipe(
                      map((user) => user?.fullName),
                      take(1)
                  )
                : of(fallback);

        return name$.pipe(map((result) => result?.trim() || fallback));
    }

    public getUser(id: string): Observable<IUser> {
        return this.appState.users.stream.pipe(
            map((users) => users.find((u) => u.id === id)),
            switchMap((user) =>
                user
                    ? of(user)
                    : this.appState.serviceTokenUsers.stream.pipe(map((tokens) => tokens.find((u) => u.id === id)))
            )
        );
    }

    // Used for checking permissions or features

    public getUsers(ids: Array<string>): Observable<Array<IUser>> {
        return this.appState.users.stream.pipe(map((users) => users.filter((u) => ids.includes(u.id))));
    }

    public getUserShortName(user?: IUser): string {
        if (!user) return null;
        const names = user.fullName.split(' ');
        return names.map((n, i) => (i === names.length - 1 ? n : n.charAt(0).toUpperCase() + '.')).join(' ');
    }

    public refreshAllUsers = async (): Promise<Array<IUser>> => {
        // Fetch users
        const users = await firstValueFrom(this.userApi.getUsers());
        // Emit new tasks
        this.appState.users.value = users;

        // Fetch service token users
        const serviceTokenUsers = await firstValueFrom(this.userApi.getServiceTokenUsers());
        // Emit new tasks
        this.appState.serviceTokenUsers.value = serviceTokenUsers;

        // Return tasks
        return users;
    };

    public createUserTenantInvitation(roles: Array<IRole>): Observable<IUserTenantInvitation> {
        return this.userApi.postInvitation(roles.map((r) => r.id));
    }

    private initialize(): void {
        this.headerService.loggedIn
            .pipe(
                take(1),
                switchMap((loggedIn) => (loggedIn ? this.readStoredActiveTenant() : of(null))),
                tap((tenantId) => (this.appState.activeTenantId.value = tenantId ?? null))
            )
            .subscribe();

        // Refresh user on login state change
        this.headerService.loggedIn
            .pipe(
                distinctUntilChanged(),
                tap((x) => console.debug('Logged in state changed', x, 'calling refreshCurrentUser')),
                tap(() => this.refreshCurrentUser())
            )
            .subscribe();
        combineLatest([this.appState.activeTenantId.distinctStream, this.appState.user.distinctStream])
            .pipe(
                filter(([tenantId, user]) => tenantId && user && user.permissions == null),
                tap(() => console.debug('TenantId changed, calling refreshCurrentUser')),
                tap(() => this.refreshCurrentUser())
            )
            .subscribe();

        // Clear data on logout, but only after having been logged in (to avoid clearing data on initial load)
        this.appState.user.distinctStream
            .pipe(
                distinctUntilChanged(),
                filter(Boolean),
                take(1),
                switchMap(() => this.appState.user.distinctStream),
                filter((user) => !user),
                tap(() => {
                    this.appState.users.value = [];
                    this.tenantService.selectTenant(null, null);
                })
            )
            .subscribe();

        // Refresh every 5 minutes
        interval(5 * 60 * 1000)
            .pipe(
                filter(() => this.appState.loggedInWithTenant.value),
                tap(() => this.refreshAllUsers())
            )
            .subscribe();

        // Refresh users on socket connect
        this.appState.socketStatus.distinctStream
            .pipe(
                tap((s) => {
                    // Don't log the same status twice.
                    if (this.socketStatus !== s.status) {
                        this.socketStatus = s.status;
                        console.debug('SocketStatus', s.status);
                    }
                }),
                filter((s) => s.status === 'CONNECTED'),
                mergeMap(() => this.refreshAllUsers())
            )
            .subscribe();
    }

    private refreshCurrentUser = async (): Promise<IUser> => {
        console.debug('Refreshing current user');
        const userObservable = combineLatest([
            this.headerService.loggedIn,
            this.appState.activeTenantId.distinctStream,
        ]).pipe(
            switchMap(([loggedIn, tenantId]) => this.loadUser(loggedIn, tenantId)),
            map(({ user, tenantId, permissionsLoaded }) => this.selectTenant(user, tenantId, permissionsLoaded)),
            switchMap(({ user, tenant, permissionsLoaded }) => this.loadPermissions(user, tenant, permissionsLoaded)),
            take(1),
            tap((user) => (this.appState.user.value = user)),
            tap((user) => {
                if (user) this.saveLanguage(user.profile.language, false);
            })
        );

        return lastValueFrom(userObservable);
    };

    /**
     * If logged in, get the logged-in user. If not, return null.
     * @param loggedIn Whether or not the user is logged in.
     * @param tenantId The tenant the user is logged in on.
     * @returns The logged-in user, tenantID and whether permissions are loaded, or null if not logged in.
     * @private
     */
    private loadUser(
        loggedIn: boolean,
        tenantId: string
    ): Observable<{ user: IUser; tenantId: string; permissionsLoaded: boolean }> {
        let user: Observable<IUser>;
        if (loggedIn) {
            user = this.acceptInvitationsByEmail.pipe(
                switchMap(() =>
                    tenantId
                        ? this.userApi
                              .getMeWithPermissions(tenantId)
                              .pipe(catchError((error) => this.handleTenantLoadError(error, tenantId)))
                        : this.userApi.getMe().pipe(
                              tap((user) => {
                                  user.permissions = null;
                                  this.appState.user.value = user;
                              })
                          )
                )
            );
        } else {
            user = of(null);
        }

        const permissionsLoaded = user.pipe(map((u) => Boolean(u?.permissions)));
        return combineLatest({ user, tenantId: of(tenantId), permissionsLoaded: permissionsLoaded });
    }

    /**
     * If we have an active tenant, select it.
     * @param user The logged-in user
     * @param tenantId The tenant the user is logged in on.
     * @param permissionsLoaded Whether or not the permissions have already been loaded.
     * @returns An array containing:
     *
     * - The logged-in user or null,
     * - The current tenantId if it's selected,
     * - Whether permissions are loaded.
     * @private
     */
    private selectTenant(
        user: IUser,
        tenantId: string,
        permissionsLoaded: boolean
    ): { user: IUser; tenant: ITenant; permissionsLoaded: boolean } {
        if (!user) return { user, tenant: null, permissionsLoaded };

        let tenant: ITenant = null;
        if (tenantId) {
            tenant = user.tenants.find((t) => t.id === tenantId);
        } else if (user.tenants.length === 1) {
            tenant = user.tenants[0];
        }

        if (tenant) {
            this.tenantService.selectTenant(user, tenant);
        }

        return { user: user, tenant: tenant, permissionsLoaded: permissionsLoaded };
    }

    /**
     *  If we have a logged-in user and an active tenant, load authorization.
     * @param user The logged-in user
     * @param tenant The tenant the user is logged in on.
     * @param permissionsLoaded Whether or not the permissions have already been loaded.
     * @returns The logged-in user with loaded permissions or null.
     * @private
     */
    private loadPermissions(user: IUser, tenant: ITenant, permissionsLoaded: boolean): Observable<IUser> {
        return !tenant || permissionsLoaded
            ? of(user)
            : this.userApi.getMeWithPermissions(tenant.id).pipe(
                  catchError((e) => {
                      console.error('Error loading user permissions, redirecting to login.', e);
                      this.headerService.logout();
                      return of(null);
                  }),
                  map((userWithPermissions) => ({ ...user, ...(userWithPermissions ?? {}) }) as IUser)
              );
    }

    private readStoredActiveTenant(): Observable<string | null> {
        const paramTenant = this.getTenantFromQueryParam();
        if (paramTenant) return of(paramTenant);
        return this.tenantService.getStoredTenant().pipe(
            take(1),
            catchError(() => of(null))
        );
    }

    private handleTenantLoadError(error: unknown, tenantId: string): Observable<IUser> {
        if (error instanceof HttpErrorResponse && (error.status === 403 || error.status === 404)) {
            console.warn(`No access to tenant '${tenantId}', logging out.`, error);
            const failingTenantCameFromQueryParam = this.getTenantFromQueryParam() === tenantId;
            const clearTenant: Observable<void> = failingTenantCameFromQueryParam
                ? of(undefined)
                : this.tenantService.clearStoredTenant();
            return clearTenant.pipe(
                tap(() => this.headerService.logout()),
                map((): IUser => null)
            );
        }

        console.error('Error loading user permissions, redirecting to login.', error);
        this.headerService.logout();
        return of(null);
    }

    private getTenantFromQueryParam(): string | null {
        return new window.URLSearchParams(window.location.search).get('tenant');
    }

    // Supports wildcards
    private containsNode(node: string | Feature, nodes: Array<string>): boolean {
        node = typeof node === 'string' ? node : Feature[node];

        if (node === '*') return true;
        const ruleStr = `^${node
            .toLowerCase()
            .split('.')
            .map((str) => (str === '*' ? '.+?' : str.replace(/[.*+\-?^${}()|[\]\\]/g, '\\$&')))
            .join('[.]')}$`;
        const rule = new RegExp(ruleStr);
        return !!nodes.map((_node) => _node.toLowerCase()).find((_node) => rule.test(_node));
    }

    private observable(user: TAsyncUser): Observable<IUser> {
        return user instanceof Observable ? user : user.distinctStream;
    }
}
